import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTheme, Box, Card, Button, Typography,
  CardContent, useMediaQuery,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import Title from '../../components/Title';
import { formatCurrency } from '../../utils/format/currency';
import { useDrawer } from '../../components/context/DrawerContext';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../components/layouts/StickyListToolbar';
import HeaderResponsive from '../../components/layouts/HeaderResponsive';
import EmptyStateMessage from '../../components/layouts/EmptyStateMessage';
import ModalOfficeBox from './modals/ModalOfficeBox';
import SimpleCustomTable from '../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow } from '../../components/table/interface';
import PermissionButton from '../../components/common/PermissionButton';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../components/table/iconActionColumnSizing';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const ReporteCajasOficina: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [datas, setDatas] = useState<any[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<{ id: number; name: string } | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const { canRead } = useResourcePermissions('/reportes/reportecajas');

  const { drawerOpen } = useDrawer();

  document.title = 'Reporte de Cajas por Oficina';

  // Solo al montar: showAlert/showLoading/hideLoading en App no son estables (nuevo render ⇒ nueva
  // referencia) y al depender de ellas el efecto se re-ejecutaba tras showLoading(), dejando el
  // loading colgado o lanzando peticiones duplicadas. Mismo patrón que administrar/oficinas/list.
  useEffect(() => {
    const fetchUserBoxes = async () => {
      showLoading();
      try {
        const response = await axios.get('/report/boxes/user');
        const boxes = response.data.data;

        const list = Array.isArray(boxes) ? boxes : [];
        const transformed = [...list]
          .sort((a: any, b: any) =>
            String(a?.office_name ?? '').localeCompare(String(b?.office_name ?? ''), 'es', {
              sensitivity: 'base',
            }),
          )
          .map((box: any, index: number) => ({
            id: index + 1,
            office_id: box.office_id,
            office: box.office_name || '',
            box: box.box_total || 0,
            principalBox: box.principal_box_balance || 0,
          }));

        setDatas(transformed);

        if (!transformed.length) {
          showAlert('No existen cajas asociadas al usuario', 'info');
        }
      } catch (error) {
        console.error('Error al obtener cajas:', error);
        showAlert('Error al obtener cajas del usuario', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchUserBoxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks desde App.tsx (ver comentario arriba)
  }, []);

  const handleOpenModal = useCallback((row: { office_id: number; office: string }) => {
    setSelectedOffice({ id: row.office_id, name: row.office });
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setSelectedOffice(null);
    setOpenModal(false);
  };

  const totalRowId = 'total-row';

  const tableRows = useMemo((): SimpleRow[] => {
    if (!datas.length) return [];
    const totalBox = datas.reduce((sum, row) => sum + Number(row.box || 0), 0);
    const totalPrincipal = datas.reduce((sum, row) => sum + Number(row.principalBox || 0), 0);
    return [
      ...datas,
      {
        id: totalRowId,
        office: 'TOTAL',
        box: totalBox,
        principalBox: totalPrincipal,
      },
    ];
  }, [datas]);

  const totalCellSx = { fontWeight: 700 } as const;

  const tableColumns: SimpleColumn[] = useMemo(
    () => [
      {
        field: 'office',
        headerName: 'Oficina',
        minWidth: isMobile ? 160 : 420,
        maxWidth: isMobile ? 200 : 480,
        wrapText: true,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            sx={row.id === totalRowId ? totalCellSx : undefined}
          >
            {String(row.office ?? '')}
          </Typography>
        ),
      },
      {
        field: 'box',
        headerName: 'Caja',
        align: 'right',
        minWidth: 80,
        maxWidth: 120,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            sx={row.id === totalRowId ? totalCellSx : undefined}
          >
            {formatCurrency(Number(row.box || 0))}
          </Typography>
        ),
      },
      {
        field: 'principalBox',
        headerName: 'Caja Principal',
        align: 'right',
        minWidth: 80,
        maxWidth: 120,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            sx={row.id === totalRowId ? totalCellSx : undefined}
          >
            {formatCurrency(Number(row.principalBox || 0))}
          </Typography>
        ),
      },
      {
        field: 'opt_view',
        headerName: '',
        ...iconActionColumnSizing(isMobile),
        renderCell: ({ row }) => {
          if (row.id === totalRowId) return null;
          return (
            <PermissionButton
              allowed={canRead}
              deniedTooltip="Sin permiso para ver el detalle de cajas"
              tooltip="Ver cajas de la oficina"
              size="small"
              variant="contained"
              color="primary"
              aria-label="Ver cajas de la oficina"
              onClick={() =>
                handleOpenModal({
                  office_id: Number(row.office_id),
                  office: String(row.office ?? ''),
                })
              }
              sx={simpleTableIconButtonSx}
            >
              <VisibilityIcon fontSize="small" />
            </PermissionButton>
          );
        },
      },
    ],
    [isMobile, canRead, handleOpenModal],
  );

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Reporte de Cajas por Oficina</Title>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.print()}
          >
            Imprimir
          </Button>
        </Box>
      </StickyListToolbar>

      <Card>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          {datas.length > 0 ? (
            <SimpleCustomTable
              rows={tableRows}
              columns={tableColumns}
              maxHeight={600}
              showBorders
              showCount
              wrapCellContent
              exportCsv
              exportFileName="reporte-cajas-oficina.csv"
              columnGroups={[{ header: 'Opciones', fields: ['opt_view'] }]}
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>

      <ModalOfficeBox
        open={openModal && Boolean(selectedOffice)}
        onClose={handleCloseModal}
        officeId={selectedOffice?.id ?? 0}
        officeName={selectedOffice?.name ?? ''}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </HeaderResponsive>
  );
};

export default ReporteCajasOficina;
